'use client';

import { Pagination } from '@snack-uikit/pagination';
import {
  Skeleton,
  SkeletonContextProvider,
  SkeletonText,
  WithSkeleton,
} from '@snack-uikit/skeleton';
import cn from 'classnames';
import { useParams, useRouter } from 'next/navigation';
import { MouseEvent, useState } from 'react';
import { Link } from 'src/components/Link';
import { Wrapper } from 'src/components/Wrapper';
import { backendHost } from 'src/global';
import { useAnalytics } from 'src/hooks/useAnalytics';
import { Layout } from 'src/layout';
import { Warp } from 'src/types';
import { NewsProps, ResponseNews } from 'src/types/backendContent';
import { Breadcrumbs } from 'src/uikit/Breadcrumbs';
import { Button } from 'src/uikit/Button';
import { Tag } from 'src/uikit/Tag';
import { formatDate } from 'src/utils/formatDate';

import { LIMIT_NEWS_CARDS } from './limitNewsCards';
import s from './News.module.scss';

export type NewsDistributionPageType = {
  newsData: NewsProps[];
  maxPages: number;
  currentPage: number;
};

type NewsCardProps = Warp & {
  className?: string;
  titleClassName?: string;
};

interface NewsCardAuthorProps {
  name: string;
  secondName: string;
  avatar: string;
  createdAt?: string;
}

function NewsCardAuthor({ name, avatar, secondName }: NewsCardAuthorProps) {
  return (
    <div className={s.author}>
      <img
        src={avatar}
        alt="avatar"
        className={s.authorAvatar}
        data-qa="avatar icon"
        width={48}
        height={48}
      />
      <div className={s.authorInfo}>
        <span className={s.authorName}>
          {name} {secondName}
        </span>
      </div>
    </div>
  );
}

function NewsCardDate({ createdAt }: { createdAt?: string }) {
  return (
    <div className={s.time} data-qa="warp-card-date">
      {formatDate(createdAt)}
    </div>
  );
}

function NewsCard({
  title,
  cover,
  releaseDate,
  target,
  href,
  authors,
  categoryTitle,
  categories,
}: NewsCardProps) {
  const [imgSrc, setImgSrc] = useState(cover);
  const date = releaseDate ? releaseDate : '';
  if (categories[0].slug === 'news') href = `/news/${href}`;

  return (
    <div className={s.news}>
      <div className={s.newsHeader}>
        <img
          src={imgSrc}
          alt={title}
          width={372}
          height={239}
          data-qa="warp-card-image"
          className={s.newsImg}
          onError={() =>
            setImgSrc(
              'https://cdn.cloud.ru/backend/images/warp/default_header.png',
            )
          }
        />

        <Tag
          className={s.newsBadge}
          variant={Tag.variants.Primary}
          textColor={Tag.textColors.Graphite}
          appearance={Tag.appearances['gray-80']}
          data-qa="warp-card-badge"
        >
          {categoryTitle}
        </Tag>
      </div>
      <div className={s.newsContent}>
        <NewsCardDate createdAt={date} />

        <Link
          href={href}
          target={target}
          className={cn('link', s.newsLink)}
          data-qa="news-card"
          id={`news-card-${title}`}
          analyticsOptions={{
            action: href,
            clickZone: 'body',
            clickElement: 'cardclick',
            clickContent: title,
            uniqueId: `news-card-${title}`,
            transitionType: 'inside-link',
          }}
        >
          <h2
            className={s.newsTitle}
            dangerouslySetInnerHTML={{ __html: title }}
            data-qa="warp-card-title"
          />
        </Link>

        {authors && (
          <NewsCardAuthor
            name={authors[0].name}
            avatar={authors[0].avatar}
            secondName={authors[0].secondName}
          />
        )}
      </div>
    </div>
  );
}

function NewsCards({ news }: { news: NewsProps[] | undefined }) {
  if (news) {
    return news.map((item, index) => {
      return <NewsCard key={index} {...item} />;
    });
  }
}

function SkeletonCard() {
  return (
    <div className={s.skeleton}>
      <Skeleton className={s.skeletonImg} />

      <SkeletonText
        lines={1}
        className={s.skeletonDate}
        rowClassName={s.row}
        lineClassName={s.line}
      />

      <SkeletonText
        lines={3}
        className={s.skeletonDescription}
        rowClassName={s.row}
        lineClassName={s.line}
      />
    </div>
  );
}

function SkeletonCards({ count }: { count: number }) {
  return Array(count)
    .fill(0)
    .map((_, index) => (
      <WithSkeleton key={index} skeleton={<SkeletonCard />} />
    ));
}

export function NewsDistributionPage({
  newsData,
  maxPages,
  currentPage,
}: NewsDistributionPageType) {
  const [news, setNews] = useState<NewsProps[]>(newsData);
  const [loading, setLoading] = useState(false);
  const [curPage, setCurPage] = useState(currentPage);
  const { clickAnalytics } = useAnalytics();
  const router = useRouter();
  const params = useParams();

  const handleShowMore = async () => {
    const fetchMore = async () => {
      setLoading(true);

      fetch(
        `${backendHost}/content/v1/news?page=${
          curPage + 1
        }&limit=${LIMIT_NEWS_CARDS}`,
      )
        .then((res) => {
          if (!res.ok) {
            throw Error(res.statusText);
          }
          return res.json();
        })
        .then((res: ResponseNews) => {
          setNews((prev) => [...prev, ...res.data.content]);
          globalThis?.history.pushState(null, '', `/news/${curPage + 1}`);
          setCurPage((prev) => prev + 1);
        })
        .catch((e) => {
          // Обязательный лог
          // eslint-disable-next-line no-console
          console.error(`Failed to fetch news: ${e.message}`);
        })
        .finally(() => setLoading(false));
    };

    clickAnalytics({
      action: 'click',
      clickZone: 'body',
      clickElement: 'button',
      clickContent: mock.buttonTitle,
      uniqueId: 'news-button-show-more',
      transitionType: 'inside-link',
    });

    await fetchMore();
  };

  const handleChangePage = (
    page: number,
    event?: MouseEvent<HTMLAnchorElement>,
  ) => {
    event?.preventDefault();
    if (page === curPage) return;
    const rout = page === 1 ? `/news` : `/news/${page}`;

    clickAnalytics({
      action: rout,
      clickZone: 'body',
      clickElement: 'button',
      clickContent: page.toString(),
      uniqueId: `news-pagination-button-${page}`,
      transitionType: 'inside-link',
    });

    const newsParam = params?.slug;

    if (!newsParam && page === 1) {
      window.location.pathname = '/news';
    }

    if (newsParam === page.toString()) {
      window.location.pathname = `/news/${newsParam}`;
    }

    router.push(rout);
  };

  const mock = {
    seo: {
      metaTitle: `Новости облачных технологий провайдера Cloud.ru`,
      metaDescription: `Новости облачных технологий провайдера Cloud.ru. Надежность, качество и безопасность!`,
    },
    all: {
      id: '0',
      title: 'Все',
      slug: 'all',
    },
    breadcrumbs: [
      {
        title: 'Главная',
        link: '/',
      },
      { title: 'Новости', link: '' },
    ],
    header: {
      title: 'Новости Cloud.ru',
    },
    buttonTitle: 'Показать еще',
  };

  if (curPage !== 1) {
    mock.seo.metaTitle = `${mock.seo.metaTitle} | Страница ${curPage}`;
    mock.seo.metaDescription = `${mock.seo.metaDescription} | Страница ${curPage}`;
  }

  const hrefFormatter = (page: number) =>
    page === 1 ? '/news' : `/news/${page}`;

  return (
    <Layout>
      <div className={s.root}>
        <Wrapper>
          <div className={s.header}>
            <Breadcrumbs links={mock.breadcrumbs} className={s.breadcrumbs} />
            <h1 className={s.headerTitle}>{mock.header.title}</h1>
          </div>
        </Wrapper>
        <Wrapper>
          <section className={s.container}>
            <div className={s.cards}>
              {news && news.length > 0 && <NewsCards news={news} />}

              <SkeletonContextProvider loading={loading}>
                <SkeletonCards count={LIMIT_NEWS_CARDS} />
              </SkeletonContextProvider>
            </div>

            {maxPages > 1 && (
              <Pagination
                page={curPage}
                total={maxPages}
                onChange={handleChangePage}
                hrefFormatter={hrefFormatter}
                variant="link"
              />
            )}

            {curPage < maxPages && (
              <Button
                className={s.showMore}
                variant={Button.variants.Outline}
                size={Button.sizes.Big}
                onClick={handleShowMore}
                data-qa="show-more-btn"
                analyticsOptions={{
                  action: 'click',
                  clickZone: 'body',
                  clickElement: 'button',
                  clickContent: mock.buttonTitle,
                  uniqueId: `warpcard-button-${mock.buttonTitle}`,
                  transitionType: 'inside-link',
                }}
              >
                {mock.buttonTitle}
              </Button>
            )}
          </section>
        </Wrapper>
      </div>
    </Layout>
  );
}

export function NewsPage({
  newsData,
  maxPages,
  currentPage,
}: NewsDistributionPageType) {
  return (
    <NewsDistributionPage
      newsData={newsData}
      maxPages={maxPages}
      currentPage={currentPage}
    />
  );
}
